import React, { useMemo, useState, useEffect } from "react";
import { useLocation, Redirect, Route, Switch } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import PersonalArea from "pages/PersonalArea";
import PostsPagePersonalArea from "pages/PostsPagePersonalArea";
import Viewer from "pages/PostViewer";
import CompanyAdmin from "pages/CompanyAdmin";
import UserBrands from "pages/UserBrands";
import PostsPageSuperAdmin from "pages/PostsPageSuperAdmin";
import SuperAdmin from "pages/SuperAdmin";
import SuperAdminSettings from "pages/SuperAdminSettings";
import SuperAdminBrands from "pages/SuperAdminBrands";
import Authorization from "pages/Authorization";
import RegistrationUserCompanyAdmin from "pages/RegistrationUserCompanyAdmin";
import RegistrationUser from "pages/RegistrationUser";
import ResetPassword from "pages/ResetPassword";
import Share from "pages/Share";
// import LandingPageTemplate from "pages/LandingTemplate";
import Privacy from "pages/Privacy";
import Terms from "pages/Terms";


import { Maybe } from "helpers/maybeFunctor";
import "../pages/LandingTemplate/LandingTemplate.scss";

import "./App.scss";
import Header from "../components/Header";
import Breadcrumbs from "../components/Breadcrumbs";
import { MDBBox } from "mdbreact";
import DownloadSoftwareModal from "../components/DownloadSoftwareModal";
import { googleAnalytics4 } from "../helpers/ga4Helper";
import Thankyou from "pages/Thankyou";
import Brands from "pages/configuration/brands";
import Users from "pages/configuration/users";
import Statuses from "pages/configuration/statuses";
import Permissions from "pages/configuration/permisions";
import RequestAccess from "pages/configuration/requestAccess";
import Profile from "pages/Profile";
import UserPermissions from "pages/configuration/user_permissions";
import BrandSettings from "pages/configuration/brandSettings";
import HotTopics from "pages/HotTopics/HotTopics";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import SuperAdminPermissions from "pages/SuperAdmin/SuperAdminPermissions";
import { setSidebarStatus } from "actions/user";
import FilterView from "pages/FilterView";

import Dashboard from "pages/Dashboard";
import Analytics from "pages/Analytics";

import MyApp from "learning/pages/_app";
import { tokenRef } from "api";
import ActivityLog from "pages/ActivityLog/ActivityLog";
import Earnings from "pages/Earnings";
import Affiliations from "pages/configuration/Affiliations";
import CompanySettings from "pages/configuration/CompanySettings";
import { SupportChat } from "pages/Chat/SupportChat";
import SocialMediaConnect from "pages/SocialMediaConnect";
import SocialMediaAccounts from "pages/SocialMediaAccounts";
import AllPostsPage from "pages/AllPostsPage";
import PostAnalytics from "pages/PostAnalytics";
import SeoAnalytics from "pages/SeoAnalytics";
import EmailAnalytics from "pages/EmailAnalytics";
import IdeasPage from "pages/Ideas/IdeasPage";
import CommunityPage from "pages/Community/CommunityPage";
import WorkerHealth from "pages/WorkerHealth";
import AutomationPipeline from "pages/AutomationPipeline";
import AdminCostControl from "pages/AdminCostControl";
import CustomWorkers from "pages/CustomWorkers";
import AdminCustomWorkers from "pages/AdminCustomWorkers";
import ServerAccess from "pages/ServerAccess";
import AiOperations from "pages/AiOperations";
import EmailManagement from "pages/EmailManagement";
import SeoManagement from "pages/SeoManagement";
import SeoPublishTargets from "pages/SeoManagement/PublishTargets";
import ChatManagement from "pages/ChatManagement";
import LeadAcquisition from "pages/LeadAcquisition";
import FeatureRequests from "pages/FeatureRequests";
import AlertPreferences from "pages/AlertPreferences/AlertPreferences";
import Documentation from "pages/Documentation/Documentation";
import AllCompaniesSettings from "pages/AllCompaniesSettings/AllCompaniesSettings";
import PlatformConfig from "pages/PlatformConfig/PlatformConfig";
import SelfConstruct from "pages/SelfConstruct/SelfConstruct";
import ClaudeAuth from "pages/ClaudeAuth";
import SuperAdminActivity from "pages/SuperAdminActivity";
import TrialBanner from "../components/TrialBanner/TrialBanner";
import OnboardingTour from "../components/OnboardingTour/OnboardingTour";
import NewsletterManagement from "pages/NewsletterManagement";
import InvoiceManagement from "pages/InvoiceManagement";
import { Customers as CRMCustomers, Prospects as CRMProspects } from "pages/CRM";
import { computeFinalPermissions, hasAdminAccess } from "helpers/finalPermissions";



const App = (props) => {
  const dispatch = useDispatch();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = computeFinalPermissions({
    userInfo: userInfoRedux,
    allPermission: allPermissionRedux,
    companyPermission: companyPermissionRedux,
    userPermission: usersPermissionRedux,
  });


  const maybeEmail = Maybe.of(userInfoRedux).map(
    (userInfo) => userInfo.email
  ).value;

  const maybeUserID = Maybe.of(userInfoRedux).map(
    (userInfo) => userInfo.id
  ).value;

  const maybeSuperAdmin = Maybe.of(userInfoRedux).map(
    (userInfo) => userInfo.superAdmin
  ).value;

  const maybeCompanyAdmin = Maybe.of(userInfoRedux).map(
    (userInfo) => userInfo.companyAdmin
  ).value;

  const adminFlag = maybeSuperAdmin || maybeCompanyAdmin;

  // Admin access gates the entire admin surface (side menu + /admin URLs),
  // exactly like a feature gate. True for super/company admins or any user
  // holding the adminSectionAccess grant. A user without it is redirected
  // away from every admin page (the Switch falls through to /personal-area),
  // mirroring how the admin side menu now hides for them. Kept in lock-step
  // with buildAdminMenu so the menu and the routes can never disagree.
  const adminAccess = hasAdminAccess({
    userInfo: userInfoRedux,
    finalPermissions: FinalPermissions,
  });

  const [modal, setModal] = useState("");

  const location = useLocation();

  useEffect(() => {
    dispatch(setSidebarStatus(false));
  }, [location]);

  const pages = useMemo(() => {
    return (
      <div style={{ margin: "auto" }}>
        <Switch>
          <Route path="/privacy" exact component={Privacy} />
          <Route path="/terms" exact component={Terms} />
          {/* Public route so the worker's re-auth email link renders even when
              the super-admin isn't logged in. The page + server enforce access
              (super-admin session OR a valid signed link). */}
          <Route path="/admin/claude-auth" exact component={ClaudeAuth} />
          {/* <Route path="/social-media-accounts" exact component={SocialMediaAccounts} /> */}
          {/* <Route
            path="/social/:platform/callback" // /:code/:state
         //   exact
            render={({ match }) => {
              return (
                <SocialMediaConnect
                platform={match.params.platform}
                code={match.params.code}
                state={match.params.state}
                
                />
              );
            }}
          /> */}

          <Route
  path="/social-media-accounts/:brandId?/:brandSettingsId?"
  exact
  render={({ match, location }) => {
    const qs = new URLSearchParams(location.search);
    const brandId = match.params.brandId || qs.get('brandId') || undefined;
    const brandSettingsId =
      match.params.brandSettingsId || qs.get('brandSettingsId') || undefined;

    return (
      <SocialMediaAccounts
        brandId={brandId}
        brandSettingsId={brandSettingsId}
      />
    );
  }}
/>
// Social Media OAuth callback routes
{/* <Route 
  path="/social/:platform/callback" 
  exact 
  render={({ match }) => {
    return <SocialMediaConnect platform={match.params.platform} />;
  }} 
/> */}
<Route
  path="/social/:platform/callback"
  exact
  render={({ match, location }) => {
    const qs = new URLSearchParams(location.search);
    return (
      <SocialMediaConnect
        platform={match.params.platform}
        // pass through useful context for correct brand scoping
        brandId={qs.get('brandId') || undefined}
        brandSettingsId={qs.get('brandSettingsId') || undefined}
        accountId={qs.get('accountId') || undefined}
        // SocialMediaConnect already reads connectId/api/ui from location.search,
        // but passing connectId is harmless if you want to use it directly.
        connectId={qs.get('connectId') || undefined}
      />
    );
  }}
/>

// Alternative route for direct social connections
{/* <Route 
  path="/social/connect/:platform" 
  exact 
  render={({ match }) => {
    return <SocialMediaConnect platform={match.params.platform} />;
  }} 
/> */}

<Route
  path="/social/connect/:platform"
  exact
  render={({ match, location }) => {
    const qs = new URLSearchParams(location.search);
    return (
      <SocialMediaConnect
        platform={match.params.platform}
        brandId={qs.get('brandId') || undefined}
        brandSettingsId={qs.get('brandSettingsId') || undefined}
        accountId={qs.get('accountId') || undefined}
      />
    );
  }}
/>

          <Route path="/dashboard" component={Dashboard} exact />
          <Route path="/dashboard/analytics" component={Analytics} exact />
          {maybeSuperAdmin && (
            <Route path="/worker-health" component={WorkerHealth} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/automation-pipeline" component={AutomationPipeline} exact />
          )}
          {/* Cost control is super-admin only. The apiCostView
              permission used to also unlock it for any company-admin
              granted the perm at the company level — that leaked USD
              spend to tenant admins. Role-only gating now. */}
          {maybeSuperAdmin && (
            <Route path="/admin-cost-control" component={AdminCostControl} exact />
          )}
          {adminFlag && (
            <Route path="/custom-workers" component={CustomWorkers} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/custom-workers" component={AdminCustomWorkers} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/server-access" component={ServerAccess} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/ai-operations" component={AiOperations} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/self-construct" component={SelfConstruct} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.EMAIL_MESSAGE_VIEW)) && (
            <Route path="/email-management/:tab?" component={EmailManagement} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.SEO_VIEW) || FinalPermissions?.includes(ALL_PERMISSIONS.SEO_KEYWORD_VIEW)) && (
            <Route path="/seo-management/publish-targets" component={SeoPublishTargets} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.SEO_VIEW) || FinalPermissions?.includes(ALL_PERMISSIONS.SEO_KEYWORD_VIEW)) && (
            <Route path="/seo-management/:tab?" component={SeoManagement} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.CHAT_INBOX_VIEW) || FinalPermissions?.includes(ALL_PERMISSIONS.CHAT_CONVERSATION_VIEW)) && (
            <Route path="/chat-management/:tab?" component={ChatManagement} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.LEAD_ACQUISITION_VIEW) || FinalPermissions?.includes(ALL_PERMISSIONS.LEAD_CRAWLER_VIEW)) && (
            <Route path="/lead-acquisition/:tab?" component={LeadAcquisition} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.NEWSLETTER_SCHEDULE_VIEW) || FinalPermissions?.includes(ALL_PERMISSIONS.NEWSLETTER_ISSUE_VIEW)) && (
            <Route path="/newsletter-management/:tab?" component={NewsletterManagement} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.INVOICE_VIEW)) && (
            <Route path="/invoice-management/:tab?" component={InvoiceManagement} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.EMAIL_CONTACT_VIEW)) && (
            <Route path="/crm/customers" component={CRMCustomers} exact />
          )}
          {(maybeSuperAdmin || FinalPermissions?.includes(ALL_PERMISSIONS.EMAIL_CONTACT_VIEW)) && (
            <Route path="/crm/prospects" component={CRMProspects} exact />
          )}
          {adminFlag && (
            <Route path="/admin/feature-requests" component={FeatureRequests} exact />
          )}
          {adminFlag && (
            <Route path="/admin/alert-preferences" component={AlertPreferences} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/documentation" component={Documentation} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/all-companies-settings" component={AllCompaniesSettings} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/admin/platform-config" component={PlatformConfig} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/super-admin-activity" component={SuperAdminActivity} exact />
          )}
          {/* /super-admin-companies removed — package toggle is now part of
              /super-admin/permissions/:idCompany (the existing canonical
              per-company admin page reachable from the All Companies list
              and the sidebar "Company Settings" link). */}
          <Route path="/personal-area" exact component={PersonalArea} />
          <Route path="/all-posts" exact component={AllPostsPage} />
          {FinalPermissions?.includes(ALL_PERMISSIONS.POST_VIEW) && (
            <Route path="/post-analytics" exact component={PostAnalytics} />
          )}
          <Route path="/seo-analytics" exact component={SeoAnalytics} />
          <Route path="/email-analytics" exact component={EmailAnalytics} />
          <Route path="/share-brand/:shareId" exact component={Share} />
          <Route
            path="/personal-area/brands/:brandId/posts"
            exact
            render={({ match }) => {
              const { brandId } = match.params;
              return <PostsPagePersonalArea brandId={brandId} />;
            }}
          />
          <Route
            path="/all-ideas"
            exact
            component={IdeasPage}
          />
          <Route
            path="/personal-area/brands/:brandId/ideas"
            exact
            render={({ match }) => {
              const { brandId } = match.params;
              return <IdeasPage brandId={brandId} />;
            }}
          />
          {/* Community Management — Prospects (crawler-found) + Active
              (approved) submenus, optionally scoped to a single brand.
              render=, NEVER an inline arrow in the component prop: that is a new
              TYPE on every App render, so React Router remounted the whole page
              each time App re-rendered — Community Analytics fetched every
              section 3–4× per visit, and open drawers closed by themselves.
              Guarded by App/__tests__/noInlineRouteComponents.test.js. */}
          <Route path="/community/prospects" exact
            render={() => <CommunityPage view="prospect" />} />
          <Route path="/community/active" exact
            render={() => <CommunityPage view="active" />} />
          <Route path="/community/channels" exact
            render={() => <CommunityPage view="channels" />} />
          <Route path="/community/inbox" exact
            render={() => <CommunityPage view="inbox" />} />
          <Route path="/community/contacts" exact
            render={() => <CommunityPage view="contacts" />} />
          <Route path="/community/automation" exact
            render={() => <CommunityPage view="automation" />} />
          <Route path="/community/connectors" exact
            render={() => <CommunityPage view="connectors" />} />
          <Route path="/community/analytics" exact
            render={() => <CommunityPage view="analytics" />} />
          {/* Job applications — the BidLight lead-acquisition channel's
              pipeline: applied → communication → interview → contract. */}
          <Route path="/community/jobs" exact
            render={() => <CommunityPage view="jobs" />} />
          <Route path="/personal-area/brands/:brandId/community/inbox" exact
            render={({ match }) => <CommunityPage view="inbox" brandId={match.params.brandId} />} />
          <Route path="/personal-area/brands/:brandId/community/channels" exact
            render={({ match }) => <CommunityPage view="channels" brandId={match.params.brandId} />} />
          <Route path="/personal-area/brands/:brandId/community/prospects" exact
            render={({ match }) => <CommunityPage view="prospect" brandId={match.params.brandId} />} />
          <Route path="/personal-area/brands/:brandId/community/active" exact
            render={({ match }) => <CommunityPage view="active" brandId={match.params.brandId} />} />
          <Route path="/personal-area/brands/:brandId/community/connectors" exact
            render={({ match }) => <CommunityPage view="connectors" brandId={match.params.brandId} />} />
          <Route
            path="/personal-area/brands/:brandId/posts/:postId"
            exact
            render={({ match }) => {
              return (
                <Viewer
                  brandId={match.params.brandId}
                  postId={match.params.postId}
                  // token={match.params.token}
                  personal={true}
                />
              );
            }}
          />
          <Route
            path="/personal-area/brands/:brandId/posts/:postId/:token"
            exact
            render={({ match }) => {
              return (
                <Viewer
                  brandId={match.params.brandId}
                  postId={match.params.postId}
                  token={match.params.token}
                  personal={true}
                />
              );
            }}
          />




          {adminFlag ? (
            <Route
              path="/super-admin/users/:companyId"
              render={({ match }) => {
                return <CompanyAdmin companyId={match.params.companyId} />;
              }}
              exact
            />
          ) : null}
          {adminFlag ? (
            <Route
              exact
              path="/super-admin/users/:companyId/:idUser"
              render={({ match }) => {
                const { idUser, companyId } = match.params;
                return <UserBrands idUser={idUser} companyId={companyId} />;
              }}
            />
          ) : null}
          {adminFlag ? (
            <Route
              path="/super-admin/users/:companyId/:idUser/:brand"
              exact
              render={({ match }) => {
                const { brand, idUser, companyId } = match.params;
                return <PostsPageSuperAdmin brandId={brand} idUser={idUser} companyId={companyId} />;
              }}
            />
          ) : null}
          {/* WARNING: each Route under <Switch> MUST be a direct
              child. Wrapping in <>…</> Fragments breaks Switch — it
              picks the Fragment as the first "matching" element (no
              path, so it falls back to the parent context match) and
              stops iterating. Every Route AFTER the Fragment becomes
              unreachable. That's why the /admin/statuses, /admin/
              brands-settings, /admin/user-permissions pages went
              blank for super-admins — Switch never reached them.
              Keep these two routes flat, no Fragment. */}
          {maybeSuperAdmin && (
            <Route path="/super-admin" component={SuperAdmin} exact />
          )}
          {maybeSuperAdmin && (
            <Route path="/super-admin/settings" component={SuperAdminSettings} exact />
          )}
          {maybeSuperAdmin ? (
            <Route
              path="/super-admin/brands/:idCompany"
              component={SuperAdminBrands}
              exact
            />
          ) : null}
          {maybeSuperAdmin ? (
            <Route
              path="/super-admin/permissions/:idCompany"
              component={SuperAdminPermissions}
              exact
            />
          ) : null}

          {maybeSuperAdmin ? (
            <Route path="/admin/affiliations" component={Affiliations} exact />
          ) : null}
          {adminAccess && FinalPermissions?.includes(ALL_PERMISSIONS.BRAND_VIEW) ? (
            <Route path="/admin/brands-settings" component={BrandSettings} exact />
          ) : null}
          {/* Brands → Hot Topics submenu — gated on its OWN permission
              (HOT_TOPIC_VIEW), not BRAND_VIEW, so a user with Brands access but
              no Hot Topics grant can't reach the page. Mirrors Status/Users.
              Also behind adminAccess: it lives in the admin area. */}
          {adminAccess && FinalPermissions?.includes(ALL_PERMISSIONS.HOT_TOPIC_VIEW) ? (
            <Route path="/admin/brands-settings/hot-topics" component={HotTopics} exact />
          ) : null}

          {adminAccess && FinalPermissions?.includes(ALL_PERMISSIONS.USER_VIEW) ? (
            <Route path="/users" component={Users} exact />
          ) : null}
          {adminAccess && FinalPermissions?.includes(ALL_PERMISSIONS.STATUS_VIEW) ? (
            <Route path="/admin/statuses" component={Statuses} exact />
          ) : null}

          {/* {FinalPermissions?.includes(
            ALL_PERMISSIONS.BRAND_EXPORT_SETTINGS_VIEW
          ) ? (
            <Route
              path="/admin/brand-settings"
              component={BrandSettings}
              exact
            />
          ) : null} */}

          {adminAccess && FinalPermissions?.includes(ALL_PERMISSIONS.USER_VIEW) ? (
            <Route
              path="/admin/user-permissions"
              component={UserPermissions}
              exact
            />
          ) : null}

          {adminAccess ? (
            <Route
              path="/admin/company-settings"
              component={CompanySettings}
              exact
            />
          ) : null}

          {/* Support Chat is the admin support console (see SUPER_ADMIN menu),
              not a customer-facing widget — keep it super-admin only so the
              route matches its menu gate. */}
          {maybeSuperAdmin ? (
            <Route path="/admin/support-chat" component={SupportChat} exact />
          ) : null}

          {FinalPermissions?.includes(ALL_PERMISSIONS.ASSET_MANAGEMENT_VIEW) ? (
           null
          ) : null}

          {FinalPermissions?.includes(ALL_PERMISSIONS.FILTER_VIEW) ? (
            <Route path="/filter-view" component={FilterView} exact />
          ) : null}


          {FinalPermissions?.includes(ALL_PERMISSIONS.AFFILIATIONS_VIEW) ? (
            <Route path="/earnings" component={Earnings} exact />
          ) : null}

          {adminAccess ? (
            <Route path="/activity-log" component={ActivityLog} exact />
          ) : null}

          <Route
            path="/learning"
            render={() => {
              return <MyApp anonymous={false} userInfoRedux={userInfoRedux} />;
            }}
          />
          <Route path="/permissions" component={Permissions} exact />
          <Route path="/profile" component={Profile} exact />
          <Route path="/request_access" component={RequestAccess} exact />
          <Redirect to="/personal-area/" />
        </Switch>
      </div>
    );
  }, [adminFlag, adminAccess, maybeSuperAdmin, usersPermissionRedux]);

  if (maybeEmail) {
    return (
      <div>
        {googleAnalytics4(maybeUserID)}
        <>
         {/* Driven by x-trial-days-remaining + x-subscription-status response
             headers; auto-hides when the company is healthy. */}
         <TrialBanner />
         {/* First-run product tour (dismissible). Hidden if the user
             has already dismissed/completed it, or if the request 401s. */}
         <OnboardingTour />
         <Header setModal={setModal} />
         <Breadcrumbs />

          <MDBBox>
            {/* <DownloadSoftwareModal
              isOpen={modal === "DownloadSoftware"}
              toggle={() => setModal("")}
            /> */}
            {pages}
          </MDBBox>
        </>
      </div>
    );
  }

  return (
    <div>
      {/* <DownloadSoftwareModal
        isOpen={modal === "DownloadSoftware"}
        toggle={() => setModal("")}
      /> */}
      {googleAnalytics4(maybeUserID)}
      <Switch>
      <Route
          path="/registration-user/:key"
          exact
          render={({ match }) => {
            return <RegistrationUser key={match.params.key} />;
          }}
        />
        <Route path="/privacy" exact component={Privacy} />
        <Route path="/terms" exact component={Terms} />

        <Route path="/" component={Authorization} exact />
       

        <Route path="/thank-you" component={Thankyou} exact />
        <Route path="/authorization" component={Authorization} exact />
        <Route
          path="/learning"
          render={() => {
            return (
              <>
                <Header setModal={setModal} anonymous={true} />
                <MyApp anonymous={true} />
              </>
            );
          }}
        />

        <Route
          path="/share-brand/:shareId"
          exact
          render={() => {
            return <Share anonymous={true} />;
          }}
        />
        <Route
          path="/registration-user-admin/:key"
          exact
          render={({ match }) => {
            return <RegistrationUserCompanyAdmin key={match.params.key} />;
          }}
        />
        <Route
          path="/registration-user/:key"
          exact
          render={({ match }) => {
            return <RegistrationUser key={match.params.key} />;
          }}
        />
        <Route
          path="/reset-password/:key/:token"
          exact
          render={({ match }) => {
            return <ResetPassword key={match.params.key} />;
          }}
        />
        <Route
          path="/personal-area/brands/:brandId/posts/:postId/:token"
          exact
          render={({ match }) => {
            return (
              <Viewer
                brandId={match.params.brandId}
                postId={match.params.postId}
                token={match.params.token}
                personal={true}
              />
            );
          }}
        />


        <Redirect to="/authorization" />
      </Switch>
    </div>
  );
};

export default App;
